import React, { useState } from "react";
import { Plus, Trash2, GripVertical, X, Save, HelpCircle } from "lucide-react";
import { upsertAnamneseTemplate, deleteAnamneseTemplate } from "@/lib/db";

/**
 * AnamneseCustomManager
 * Manages custom anamnese questions for a professional.
 * 
 * Props:
 *   templates    — array of current template questions
 *   onUpdated    — callback(newTemplates) after create/delete
 */
export default function AnamneseCustomManager({ templates = [], onUpdated }) {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ question_label: "", question_hint: "" });
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const handleAdd = async () => {
    if (!form.question_label.trim()) return;
    setSaving(true);
    try {
      const created = await upsertAnamneseTemplate({
        question_label: form.question_label.trim(),
        question_hint: form.question_hint.trim() || null,
        section: "custom",
        position: templates.length,
      });
      setAdding(false);
      setForm({ question_label: "", question_hint: "" });
      onUpdated([...templates, created]);
    } catch (e) {
      alert("Erro ao salvar pergunta: " + (e.message || "Erro desconhecido"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Remover esta pergunta do seu modelo padrão?")) return;
    setDeletingId(id);
    try {
      await deleteAnamneseTemplate(id);
      onUpdated(templates.filter((t) => t.id !== id));
    } catch (e) {
      alert("Erro ao remover: " + (e.message || "Erro desconhecido"));
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="ds-card p-0 overflow-hidden ds-fade-up">
      {/* Header */}
      <div className="px-6 py-4 border-b border-[hsl(var(--border-default))] bg-[hsl(var(--bg-base))]/50 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-violet-50 text-violet-600 flex items-center justify-center">
            <HelpCircle className="w-4 h-4" />
          </div>
          <div>
            <h4 className="text-sm font-bold uppercase tracking-wider text-[hsl(var(--text-primary))]">
              Perguntas Personalizadas
            </h4>
            <p className="text-[10px] text-[hsl(var(--text-tertiary))]">
              Perguntas que você adicionou ao seu modelo padrão de anamnese
            </p>
          </div>
        </div>
        <button
          onClick={() => setAdding(!adding)}
          className={`flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-lg transition-all ${
            adding
              ? "bg-red-50 text-red-600 hover:bg-red-100"
              : "bg-[hsl(var(--brand-light))] text-[hsl(var(--brand))] hover:bg-[hsl(var(--brand))]/20"
          }`}
        >
          {adding ? (
            <><X className="w-3.5 h-3.5" /> Cancelar</>
          ) : (
            <><Plus className="w-3.5 h-3.5" /> Adicionar Pergunta</>
          )}
        </button>
      </div>

      <div className="p-6 space-y-4">
        {/* Add form */}
        {adding && (
          <div className="p-4 bg-[hsl(var(--bg-base))] rounded-xl border-2 border-[hsl(var(--brand))]/20 space-y-3 ds-fade-up">
            <div className="text-xs font-bold text-[hsl(var(--brand))] uppercase tracking-wider mb-2">
              Nova pergunta personalizada
            </div>
            <div>
              <label className="text-xs font-medium text-[hsl(var(--text-secondary))] mb-1 block">
                Pergunta / Label *
              </label>
              <input
                className="ds-input"
                placeholder="Ex: Você pratica atividade física regularmente?"
                value={form.question_label}
                onChange={(e) => setForm({ ...form, question_label: e.target.value })}
                onKeyDown={(e) => e.key === "Enter" && handleAdd()}
                autoFocus
              />
            </div>
            <div>
              <label className="text-xs font-medium text-[hsl(var(--text-secondary))] mb-1 block">
                Dica / Placeholder (opcional)
              </label>
              <input
                className="ds-input"
                placeholder="Ex: Tipo de atividade, frequência semanal..."
                value={form.question_hint}
                onChange={(e) => setForm({ ...form, question_hint: e.target.value })}
              />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button
                onClick={() => { setAdding(false); setForm({ question_label: "", question_hint: "" }); }}
                className="ds-btn-ghost text-xs px-3 py-1.5"
              >
                Cancelar
              </button>
              <button
                onClick={handleAdd}
                disabled={saving || !form.question_label.trim()}
                className="ds-btn-primary text-xs px-4 py-1.5 gap-1.5"
              >
                <Save className="w-3.5 h-3.5" />
                {saving ? "Salvando..." : "Salvar Pergunta"}
              </button>
            </div>
          </div>
        )}

        {/* List of existing templates */}
        {templates.length === 0 && !adding ? (
          <div className="text-center py-8 text-sm text-[hsl(var(--text-tertiary))]">
            <HelpCircle className="w-10 h-10 mx-auto mb-3 opacity-20" />
            <p className="font-medium">Nenhuma pergunta personalizada ainda.</p>
            <p className="text-[11px] mt-1">
              Clique em <strong>+ Adicionar Pergunta</strong> para criar campos extras
              que aparecerão em todas as anamneses dos seus pacientes.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {templates.map((t, i) => (
              <div
                key={t.id}
                className="group flex items-start gap-3 p-3 rounded-xl border border-[hsl(var(--border-default))] hover:border-violet-200 bg-white transition-colors"
              >
                <GripVertical className="w-4 h-4 mt-0.5 text-[hsl(var(--text-tertiary))] opacity-40 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-[hsl(var(--text-primary))]">
                    {t.question_label}
                  </div>
                  {t.question_hint && (
                    <div className="text-[11px] text-[hsl(var(--text-tertiary))] italic mt-0.5">
                      → {t.question_hint}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => handleDelete(t.id)}
                  disabled={deletingId === t.id}
                  className="p-1.5 rounded-lg text-[hsl(var(--text-tertiary))] hover:text-red-500 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-all"
                  title="Remover pergunta"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
